"use client";

import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import { Divider } from "@/components/ui/Divider";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerTitle,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { Slot } from "@frend-digital/ui";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { type ComponentProps, Suspense, useEffect, useRef } from "react";
import { CartContent } from "./CartContent";
import { CartItems } from "./CartItems";
import { CartSummary } from "./CartSummary";
import styles from "./cart.module.css";
import { CartUpsell } from "./CartUpsell";
import { useTranslations } from "@/i18n/client";

export const useCartModal = () => {
	const searchParams = useUrlSearchParams({
		replace: true,
	});

	const open = searchParams.get("cart") === "open";

	const setOpen = (open: boolean) => {
		if (open) {
			searchParams.set("cart", "open");
		} else {
			searchParams.delete("cart");
		}
	};

	return [open, setOpen] as const;
};

export const CartModal = () => {
	const [open, setOpen] = useCartModal();
	const t = useTranslations();

	return (
		<DrawerRoot open={open} onOpenChange={setOpen}>
			<DrawerViewport title={t("Your cart")} className={styles.viewport} lock>
				<DrawerContent className={styles.cartContent}>
					<DrawerClose aria-label={t("Close cart")} className={styles.close} />
					<DrawerHeader className={styles.cartInline}>
						<DrawerTitle className={styles.title}>
							<Translate>Your cart</Translate>
						</DrawerTitle>
					</DrawerHeader>
					<Divider />
					<DrawerMain asChild>
						<CartContent className={styles.cartItems}>
							<div className={styles.cartItemsInner}>
								<CartItems />
							</div>
							<CartUpsell />
						</CartContent>
					</DrawerMain>
					<DrawerFooter className={styles.footer}>
						<CartSummary />
						<Button className={styles.checkoutBtn} href="/checkout">
							<Translate>Go to checkout</Translate>
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

const UnsuspendedCartModalTrigger = ({
	asChild,
	...props
}: ComponentProps<"button"> & {
	asChild?: boolean;
}) => {
	const [, setOpen] = useCartModal();
	const Component = asChild ? Slot : "button";

	return <Component {...props} onClick={() => setOpen(true)} />;
};

export const CartModalTrigger: typeof UnsuspendedCartModalTrigger = (props) => {
	return (
		<Suspense fallback={props.asChild ? <>{props.children}</> : null}>
			<UnsuspendedCartModalTrigger {...props} />
		</Suspense>
	);
};
